// Single beta-invite page for one app. URL: /<slug> (optionally ?lang=cs).
//
// Three gated steps: join → install → redeem. We send the user to the
// public testers Google Group to ask-to-join (Play Console only accepts
// @googlegroups.com groups, not Workspace-domain groups, so we delegate
// the join to Google's own UI and walk them to the buried button via a
// screenshot). Once they confirm install, we issue a premium code and
// show it as a QR (desktop) or deep-link button (mobile).
//
// Localized: chrome strings come from i18n.ts t(), per-app copy from
// pickBetaCopy(beta, lang). English is the default; ?lang=cs opts in.

import { useEffect, useState } from "react";
import { claimCode, loadApp, type ClaimOutcome } from "./api";
import { readCode, writeCode } from "./cookie";
import { QrCode } from "./QrCode";
import { resolveLang, pickBetaCopy, t, type Lang } from "./i18n";
import type { AppDoc, ScreenshotDoc } from "./types";

// Public redeem URL the QR encodes. The /r/:code route on this same
// site serves RedeemLanding (deeplinks into the installed app).
const REDEEM_URL_BASE = "https://beta.gntl.app/r/";

// Public testers Google Group. Anyone with a Google account can ask to
// join from this page. Membership is what Play Console gates closed-beta
// installs against.
const TESTERS_GROUP_URL = "https://groups.google.com/g/gentle-motion-testers";

type Step = "join" | "install" | "redeem";

interface FlowState {
  step: Step;
}

function readFlowState(slug: string): FlowState {
  try {
    const raw = localStorage.getItem(`betaState_${slug}`);
    if (!raw) return { step: "join" };
    const parsed = JSON.parse(raw) as FlowState;
    if (parsed.step === "join" || parsed.step === "install" || parsed.step === "redeem") {
      return parsed;
    }
  } catch {
    // ignore — fall through to fresh start
  }
  return { step: "join" };
}

function writeFlowState(slug: string, state: FlowState) {
  try {
    localStorage.setItem(`betaState_${slug}`, JSON.stringify(state));
  } catch {
    // localStorage quota or privacy mode — non-fatal, just lose persistence
  }
}

function isMobile(): boolean {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

// Embedded in-app browsers (Facebook, Instagram, etc.) are the conversion
// killer for this flow: Google blocks sign-in inside them (disallowed
// user-agent policy), so BOTH the testers-group join and the Play install
// silently fail. ~3/4 of EI's beta traffic arrived this way. We can't fix
// Google's webview block — the only escape is "open in your real browser".
function isInAppBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /FBAN|FBAV|FB_IAB|FBIOS|Instagram|Line\/|Twitter|musical_ly|TikTok|Snapchat|Pinterest|GSA\//i.test(
    ua,
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "not_found" }
  | { kind: "ready"; app: AppDoc; screenshots: ScreenshotDoc[] };

export function BetaPage({ slug }: { slug: string }) {
  const lang = resolveLang();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [flow, setFlow] = useState<FlowState>(() => readFlowState(slug));
  const [code, setCode] = useState<string | null>(() => readCode(slug));
  const [claimState, setClaimState] = useState<
    "idle" | "claiming" | "no_codes" | "error"
  >("idle");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const outcome = await loadApp(slug);
      if (cancelled) return;
      if (outcome.kind === "not_found") {
        setState({ kind: "not_found" });
        return;
      }
      if (outcome.kind === "error") {
        console.error("loadApp:", outcome.message);
        setState({ kind: "not_found" });
        return;
      }
      setState({
        kind: "ready",
        app: outcome.app,
        screenshots: outcome.screenshots,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  function advance(next: Step) {
    const merged: FlowState = { step: next };
    setFlow(merged);
    writeFlowState(slug, merged);
  }

  async function handleClaim() {
    setClaimState("claiming");
    const outcome: ClaimOutcome = await claimCode(slug);
    if (outcome.kind === "ok") {
      writeCode(slug, outcome.code);
      setCode(outcome.code);
      setClaimState("idle");
    } else if (outcome.kind === "no_codes") {
      setClaimState("no_codes");
    } else {
      setClaimState("error");
    }
  }

  if (state.kind === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--color-muted)]">
        {t(lang, "loading")}
      </div>
    );
  }

  if (state.kind === "not_found") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <h1 className="text-2xl font-bold text-[var(--color-ink)]">
          {t(lang, "not_found_title")}
        </h1>
        <p className="mt-2 text-[var(--color-muted)]">
          {t(lang, "not_found_body", { slug }).split("{slug}")[0]}
          <code className="font-mono">{slug}</code>
        </p>
      </div>
    );
  }

  const { app, screenshots } = state;
  const enabled = app.beta_enabled !== false;
  const beta = pickBetaCopy(app.beta, lang);
  const playPackage = app.play_package ?? "";
  // One-time-purchase apps have NO redeem code — premium is the Play Store
  // purchase (free for testers via Play License Testing). Step 3 finishes
  // with a "you're all set" instead of claiming a code. premium_model is a
  // structural flag on the base beta block (locale-independent).
  const oneTime = app.beta?.premium_model === "play_onetime";

  return (
    <div className="max-w-[920px] mx-auto px-5 py-12">
      {isInAppBrowser() && <InAppBrowserBanner lang={lang} />}
      <Hero lang={lang} headline={beta?.headline} subtitle={beta?.subtitle} />
      <CtaCard
        lang={lang}
        playPackage={playPackage}
        flow={flow}
        code={code}
        claimState={claimState}
        enabled={enabled}
        oneTime={oneTime}
        onAdvance={advance}
        onClaim={handleClaim}
      />
      {beta?.body && (
        <AskBox lang={lang} body={beta.body} questions={beta.ask_questions} />
      )}
      <Badges items={beta?.badges} />
      <Steps lang={lang} items={beta?.steps} />
      <Screenshots lang={lang} items={screenshots} />
      <Footer lang={lang} displayName={app.display_name ?? slug} />
    </div>
  );
}

// ── sections ─────────────────────────────────────────────────────────

// Shown only inside in-app browsers (Facebook/Instagram/…). The most
// impactful element on the page for a social-sourced audience: it tells
// them to escape to a real browser, and offers a one-tap copy of the link
// to paste there (since they often can't easily reach the ••• → "Open in
// browser" affordance).
function InAppBrowserBanner({ lang }: { lang: Lang }) {
  const [copied, setCopied] = useState(false);
  const url =
    typeof window !== "undefined" ? window.location.href : "https://beta.gntl.app";
  // The escape browser is platform-specific — Android visitors only ever see
  // "Open in Chrome", iOS only "Open in Safari" (no mixed iPhone/Android text).
  const step2 = t(lang, isIOS() ? "inapp_warn_step2_ios" : "inapp_warn_step2_android");
  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // clipboard blocked (insecure context / permissions) — non-fatal
    }
  }
  return (
    <div className="mb-6 rounded-2xl border border-amber-300 bg-amber-50 p-5 text-left">
      <h2 className="text-base font-bold text-amber-900">
        ⚠️ {t(lang, "inapp_warn_title")}
      </h2>
      <p className="mt-1.5 text-sm text-amber-900/90 leading-relaxed">
        {t(lang, "inapp_warn_intro")}
      </p>
      <ol className="mt-2.5 flex flex-col gap-1.5 text-sm text-amber-900/90">
        {[t(lang, "inapp_warn_step1"), step2].map((step, i) => (
          <li key={i} className="flex gap-2">
            <span className="font-bold tabular-nums">{i + 1}.</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <p className="mt-3 text-sm text-amber-900/90">{t(lang, "inapp_warn_or")}</p>
      <button
        type="button"
        onClick={copy}
        className="mt-2 w-full sm:w-auto bg-amber-500 text-white font-semibold text-sm py-2.5 px-5 rounded-xl hover:bg-amber-600 transition"
      >
        {copied ? t(lang, "inapp_copied") : t(lang, "inapp_copy_btn")}
      </button>
    </div>
  );
}

function Hero({
  lang,
  headline,
  subtitle,
}: {
  lang: Lang;
  headline?: string;
  subtitle?: string;
}) {
  return (
    <section className="text-center pb-6">
      <h1 className="text-3xl font-extrabold text-[var(--color-ink)]">
        {headline ?? t(lang, "hero_default")}
      </h1>
      {subtitle && (
        <p className="mt-2 text-[var(--color-muted)] max-w-[520px] mx-auto">
          {subtitle}
        </p>
      )}
    </section>
  );
}

function CtaCard({
  lang,
  playPackage,
  flow,
  code,
  claimState,
  enabled,
  oneTime,
  onAdvance,
  onClaim,
}: {
  lang: Lang;
  playPackage: string;
  flow: FlowState;
  code: string | null;
  claimState: "idle" | "claiming" | "no_codes" | "error";
  enabled: boolean;
  oneTime: boolean;
  onAdvance: (next: Step) => void;
  onClaim: () => void;
}) {
  if (!enabled) {
    return (
      <Card>
        <div className="text-sm text-[var(--color-muted)] bg-zinc-100 border border-zinc-200 rounded-xl py-3 px-4">
          {t(lang, "paused_prefix")}{" "}
          <a
            href={`mailto:support@gentle-motion.com?subject=${encodeURIComponent(t(lang, "access_subject"))}`}
            className="text-[var(--color-accent)] underline"
          >
            {t(lang, "paused_email")}
          </a>{" "}
          {t(lang, "paused_suffix")}
        </div>
      </Card>
    );
  }

  if (flow.step === "join") {
    return (
      <Card wide>
        <StepHeader lang={lang} num={1} title={t(lang, "step_join_title")} />
        <StepJoin lang={lang} onDone={() => onAdvance("install")} />
      </Card>
    );
  }

  if (flow.step === "install") {
    return (
      <Card>
        <StepHeader
          lang={lang}
          num={2}
          title={t(lang, "step_install_title")}
          onBack={() => onAdvance("join")}
        />
        <StepInstall
          lang={lang}
          playPackage={playPackage}
          oneTime={oneTime}
          onConfirm={() => {
            // One-time apps have no code to claim — skip straight to "done".
            if (!oneTime && !code && claimState === "idle") onClaim();
            onAdvance("redeem");
          }}
          onBack={() => onAdvance("join")}
        />
      </Card>
    );
  }

  // redeem (or, for one-time apps, a plain "you're all set" finish)
  return (
    <Card>
      <StepHeader
        lang={lang}
        num={3}
        title={oneTime ? t(lang, "done_onetime_title") : t(lang, "step_redeem_title")}
        onBack={() => onAdvance("install")}
      />
      {oneTime ? (
        <StepDoneOnetime lang={lang} />
      ) : (
        <StepRedeem lang={lang} code={code} claimState={claimState} onClaim={onClaim} />
      )}
    </Card>
  );
}

function Card({
  children,
  wide,
}: {
  children: React.ReactNode;
  wide?: boolean;
}) {
  const maxW = wide ? "max-w-[880px]" : "max-w-[460px]";
  return (
    <div
      className={`bg-white rounded-2xl shadow-sm border border-zinc-200 p-7 flex flex-col gap-4 ${maxW} mx-auto mb-8`}
    >
      {children}
    </div>
  );
}

function StepHeader({
  lang,
  num,
  title,
  onBack,
}: {
  lang: Lang;
  num: number;
  title: string;
  onBack?: () => void;
}) {
  return (
    <div className="flex flex-col gap-2 -mb-1">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="self-start text-xs text-zinc-500 hover:text-[var(--color-accent)] -ml-1 px-1 py-0.5"
        >
          {t(lang, "back")}
        </button>
      )}
      <div className="flex items-center gap-2.5">
        <span className="inline-flex items-center justify-center w-[28px] h-[28px] rounded-full bg-[var(--color-accent)] text-white text-xs font-bold">
          {num}
        </span>
        <h2 className="text-lg font-bold text-[var(--color-ink)]">{title}</h2>
        <div className="ml-auto text-xs font-medium text-zinc-400">
          {t(lang, "step_of", { n: num })}
        </div>
      </div>
    </div>
  );
}

// ── Step 1 — join the Google Group ────────────────────────────────────

function StepJoin({ lang, onDone }: { lang: Lang; onDone: () => void }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-zinc-600 leading-relaxed -mt-1">
        {t(lang, "join_intro")}
      </p>

      <a
        href={TESTERS_GROUP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full text-center bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl no-underline hover:bg-blue-900 transition"
      >
        {t(lang, "join_open")}
      </a>

      <img
        src="/join_this_group.png"
        alt={t(lang, "join_img_alt")}
        className="block max-w-none rounded-xl border border-zinc-200"
      />

      <button
        type="button"
        onClick={onDone}
        className="w-full bg-white border-2 border-[var(--color-accent)] text-[var(--color-accent)] font-semibold py-3 px-4 rounded-xl hover:bg-blue-50 transition"
      >
        {t(lang, "join_done")}
      </button>
    </div>
  );
}

// ── Step 2 — install confirmation ─────────────────────────────────────

function StepInstall({
  lang,
  playPackage,
  oneTime,
  onConfirm,
  onBack,
}: {
  lang: Lang;
  playPackage: string;
  oneTime: boolean;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const mobile = isMobile();
  const playUrl = playPackage
    ? `https://play.google.com/store/apps/details?id=${playPackage}`
    : null;
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-zinc-600 leading-relaxed">
        {t(lang, "install_intro")}
      </p>
      {playUrl ? (
        <a
          href={playUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl no-underline hover:bg-blue-900 transition"
        >
          {t(lang, "install_open_play")}
        </a>
      ) : (
        <div className="text-sm text-[var(--color-muted)] bg-zinc-100 rounded-xl py-3 px-4">
          {t(lang, "install_play_pending")}
        </div>
      )}
      {/* Desktop visitors have no path to their phone — a QR to the Play
          listing bridges that. Useless on mobile (you're already there),
          so only render it on desktop. */}
      {playUrl && !mobile && (
        <QrPanel value={playUrl} hint={t(lang, "install_qr_hint")} emoji="📲" />
      )}
      <button
        type="button"
        onClick={onConfirm}
        className="w-full bg-white border-2 border-[var(--color-accent)] text-[var(--color-accent)] font-semibold py-3 px-4 rounded-xl hover:bg-blue-50 transition"
      >
        {t(lang, oneTime ? "install_done_onetime" : "install_done")}
      </button>
      <button
        type="button"
        onClick={onBack}
        className="w-full text-sm text-zinc-500 hover:text-[var(--color-accent)] hover:bg-zinc-50 py-3 px-4 rounded-xl transition"
      >
        {t(lang, "install_back")}
      </button>
    </div>
  );
}

// Shared desktop QR card — a tappable white tile (so dark-mode browsers
// don't invert it) wrapping the QR, plus a hint line. Used for the
// step-2 "open on your phone" link and the step-3 share link.
function QrPanel({
  value,
  hint,
  emoji,
}: {
  value: string;
  hint: string;
  emoji?: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 pt-1">
      <a
        href={value}
        className="p-2 rounded-2xl shadow-[0_6px_16px_rgba(0,0,0,0.18)] no-underline"
        style={{ background: "#ffffff" }}
      >
        <QrCode value={value} size={150} centerEmoji={emoji} />
      </a>
      <p className="text-xs text-[var(--color-muted)] text-center max-w-[220px]">
        {hint}
      </p>
    </div>
  );
}

// ── Step 3 — code reveal ──────────────────────────────────────────────

function StepRedeem({
  lang,
  code,
  claimState,
  onClaim,
}: {
  lang: Lang;
  code: string | null;
  claimState: "idle" | "claiming" | "no_codes" | "error";
  onClaim: () => void;
}) {
  if (claimState === "claiming") {
    return (
      <p className="text-sm text-zinc-600 text-center py-2">
        {t(lang, "redeem_getting")}
      </p>
    );
  }
  if (claimState === "no_codes") {
    return (
      <div className="text-sm text-[var(--color-muted)]">
        {t(lang, "redeem_no_codes")}
      </div>
    );
  }
  if (claimState === "error" && !code) {
    return (
      <button
        type="button"
        onClick={onClaim}
        className="w-full bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl hover:bg-blue-900 transition"
      >
        {t(lang, "redeem_error_retry")}
      </button>
    );
  }
  if (!code) {
    return (
      <button
        type="button"
        onClick={onClaim}
        className="w-full bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl hover:bg-blue-900 transition"
      >
        {t(lang, "redeem_get")}
      </button>
    );
  }
  return <CodeReveal lang={lang} code={code} />;
}

// We deliberately don't render the code as text — typing it in is
// clumsy and routes redemptions through a different path. Mobile gets
// a button that hits the same redeem URL the QR encodes; desktop shows
// the QR for cross-device scan. Both land at /r/<code> (RedeemLanding)
// which bounces into the app via the Android App Link / scheme.
function CodeReveal({ lang, code }: { lang: Lang; code: string }) {
  const mobile = isMobile();
  const redeemUrl = REDEEM_URL_BASE + code.toUpperCase();
  if (mobile) {
    return (
      <div className="flex flex-col gap-3">
        <a
          href={redeemUrl}
          className="block w-full text-center bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl no-underline hover:bg-blue-900 transition"
        >
          {t(lang, "unlock_btn")}
        </a>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {/* Literal #ffffff (not Tailwind bg-white) so this frame stays
          white in forced-dark-mode browsers, where the parent card may
          render dark and otherwise leave the QR looking like a hole.
          Wrapped in <a> so mobile-on-desktop-mode users have a tap path. */}
      <div className="flex justify-center">
        <a
          href={redeemUrl}
          className="p-2 rounded-2xl shadow-[0_6px_16px_rgba(0,0,0,0.28)] no-underline"
          style={{ background: "#ffffff" }}
        >
          <QrCode value={redeemUrl} size={200} centerEmoji="❤️" />
        </a>
      </div>
      <p className="text-lg text-zinc-700 text-center leading-snug px-2">
        {t(lang, "scan_hint")}
      </p>
    </div>
  );
}

// ── Step 3 (one-time apps) — "you're all set" + share ─────────────────
// No code to redeem, so step 3 becomes useful: thank the tester and give
// them a way to pass the page to another parent. Desktop sees a QR back to
// this page; mobile gets the native share sheet (clipboard fallback).
function StepDoneOnetime({ lang }: { lang: Lang }) {
  const mobile = isMobile();
  const pageUrl =
    typeof window !== "undefined" ? window.location.href : "https://beta.gntl.app";
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-zinc-600 leading-relaxed text-center pt-1">
        {t(lang, "done_onetime_body")}
      </p>
      <div className="border-t border-zinc-100 pt-4 flex flex-col gap-3">
        <p className="text-sm font-medium text-zinc-700 text-center">
          {t(lang, "share_prompt")}
        </p>
        {mobile ? (
          <ShareButton lang={lang} url={pageUrl} />
        ) : (
          <QrPanel value={pageUrl} hint={t(lang, "share_qr_hint")} emoji="🛗" />
        )}
      </div>
    </div>
  );
}

function ShareButton({ lang, url }: { lang: Lang; url: string }) {
  const [copied, setCopied] = useState(false);
  async function onShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ url });
      } catch {
        // user dismissed the share sheet — non-fatal, nothing to do
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // clipboard blocked (insecure context / permissions) — non-fatal
    }
  }
  return (
    <button
      type="button"
      onClick={onShare}
      className="w-full bg-[var(--color-accent)] text-white font-semibold py-3 px-4 rounded-xl hover:bg-blue-900 transition"
    >
      {copied ? t(lang, "share_copied") : t(lang, "share_btn")}
    </button>
  );
}

// ── unchanged sections ────────────────────────────────────────────────

function AskBox({
  lang,
  body,
  questions,
}: {
  lang: Lang;
  body: string;
  questions?: string[];
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-zinc-200 p-6 max-w-[500px] mx-auto mb-7">
      <p className="text-[0.95rem] text-zinc-600 leading-[1.7]">{body}</p>
      {questions && questions.length > 0 && (
        <ul className="mt-4 flex flex-col gap-2 list-none">
          {questions.map((q, i) => (
            <li
              key={i}
              className="text-sm text-zinc-700 leading-snug pl-5 relative before:content-['→'] before:absolute before:left-0 before:text-[var(--color-accent)] before:font-semibold"
            >
              {q}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-5 text-right">
        <a
          href={`mailto:support@gentle-motion.com?subject=${encodeURIComponent(t(lang, "feedback_subject"))}`}
          className="inline-block py-2 px-5 bg-[var(--color-accent)] text-white font-semibold text-sm rounded-xl no-underline hover:bg-blue-900"
        >
          {t(lang, "feedback_btn")}
        </a>
      </div>
    </div>
  );
}

function Badges({
  items,
}: {
  items?: { emoji: string; label: string }[];
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex justify-center gap-3 flex-wrap mb-2">
      {items.map((b, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 bg-white py-1 px-3 rounded-full text-xs text-[var(--color-muted)] border border-zinc-200"
        >
          {b.emoji} {b.label}
        </span>
      ))}
    </div>
  );
}

function Steps({
  lang,
  items,
}: {
  lang: Lang;
  items?: { emoji: string; title: string; body: string }[];
}) {
  if (!items || items.length === 0) return null;
  return (
    <section className="py-10">
      <h2 className="text-center text-3xl font-semibold text-[var(--color-ink)] mb-7">
        {t(lang, "how_it_works")}
      </h2>
      <div className="flex gap-3 flex-col sm:flex-row">
        {items.map((step, i) => (
          <div
            key={i}
            className="flex-1 bg-white rounded-xl shadow-sm p-4 text-center"
          >
            <div className="text-4xl mb-2">{step.emoji}</div>
            <h3 className="text-sm font-semibold mb-1">{step.title}</h3>
            <p className="text-xs text-[var(--color-muted)] leading-snug">
              {step.body}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Screenshots({ lang, items }: { lang: Lang; items: ScreenshotDoc[] }) {
  if (items.length === 0) return null;
  return (
    <section className="py-10 -mx-5">
      <h2 className="text-center text-3xl font-semibold text-[var(--color-ink)] mb-8">
        {t(lang, "see_in_action")}
      </h2>
      <div className="flex items-center gap-3 overflow-x-auto px-6 pb-3 justify-center snap-x snap-mandatory">
        {items.map((s, i) => (
          <div key={s.id} className="flex items-center gap-3 flex-shrink-0">
            <div className="text-center snap-center">
              <img
                src={s.url}
                alt={s.caption}
                className="w-[200px] rounded-xl shadow-md"
              />
              {s.caption && (
                <div className="text-xs text-[var(--color-muted)] font-medium mt-1.5">
                  {s.caption}
                </div>
              )}
            </div>
            {i < items.length - 1 && (
              <div className="text-xl text-zinc-300 flex-shrink-0">→</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer({ lang, displayName }: { lang: Lang; displayName: string }) {
  return (
    <footer className="text-center py-6 text-xs text-zinc-400">
      <p>
        {displayName} {t(lang, "footer_by")}{" "}
        <a
          href="https://gentle-motion.com"
          className="text-[var(--color-muted)] hover:underline"
        >
          Gentle Motion Apps
        </a>
      </p>
    </footer>
  );
}
